import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
//import { FileText, ShoppingCart, Truck, Headphones, TrendingUp, AlertCircle } from 'lucide-react';  remplacé par :
import { FileText, ShoppingCart, Truck, Headphones, AlertCircle, Shield } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import api from '../services/api'
//import type { Invoice, Order, Shipment, Ticket } from '../types'; replacé par :
import type { Invoice, Order, Ticket } from '../types'
import { formatCurrency, formatDate } from '../utils/helpers'

export default function Dashboard() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const user = api.getUser()
  // Admins without an assigned ERP (super_admin and plain admin) have no customer_id
  // and cannot access user-facing ERP data routes — redirect them to the admin panel.
  // ERP-scoped admins (has_erp=true) browse in multi-client mode and DO have a dashboard.
  const isAdminUser = (user?.role === 'admin' || user?.role === 'super_admin') && !user?.has_erp

  const [stats, setStats] = useState({
    invoices: 0,
    orders: 0,
    shipments: 0,
    tickets: 0,
    unpaidInvoices: 0,
  })
  const [recentInvoices, setRecentInvoices] = useState<Invoice[]>([])
  const [recentOrders, setRecentOrders] = useState<Order[]>([])
  const [enabledModules, setEnabledModules] = useState<Record<string, boolean>>({})
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (isAdminUser) {
      navigate('/admin', { replace: true })
      return
    }

    const loadData = async () => {
      try {
        let modules: Record<string, boolean> = {}
        try {
          modules = await api.getEnabledModules()
        } catch {
          modules = { invoices: true, orders: true, shipments: true, tickets: true }
        }
        setEnabledModules(modules)

        type PaginatedResponse<T> = { data: T[]; pagination?: { total: number } }
        const unwrap = <T,>(res: T[] | PaginatedResponse<T>): T[] =>
          Array.isArray(res) ? res : (res?.data ?? [])
        const total = <T,>(res: T[] | PaginatedResponse<T>, arr: T[]): number =>
          Array.isArray(res) ? arr.length : (res?.pagination?.total ?? arr.length)

        const [invoicesData, ordersData, shipmentsData, ticketsData] = await Promise.all([
          modules.invoices ? api.getInvoices().catch(() => []) : Promise.resolve([]),
          modules.orders ? api.getOrders().catch(() => []) : Promise.resolve([]),
          modules.shipments ? api.getShipments().catch(() => []) : Promise.resolve([]),
          modules.tickets ? api.getTickets().catch(() => []) : Promise.resolve([]),
        ])

        const invoices = unwrap<Invoice>(invoicesData)
        const orders = unwrap<Order>(ordersData)
        const shipments = unwrap(shipmentsData)
        const tickets = unwrap<Ticket>(ticketsData)

        const unpaidInvoices = invoices.filter((inv) => inv.is_paid !== 1).length

        setStats({
          invoices: total(invoicesData, invoices),
          orders: total(ordersData, orders),
          shipments: total(shipmentsData, shipments),
          tickets: tickets.filter((t) => t.status !== 9 && t.status !== '9').length,
          unpaidInvoices,
        })

        setRecentInvoices(invoices.slice(0, 5))
        setRecentOrders(orders.slice(0, 5))
      } catch (error) {
        console.error('Erreur chargement données:', error)
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [isAdminUser, navigate])

  const allStatCards = [
    {
      name: t('dashboard.invoices'),
      value: stats.invoices,
      icon: FileText,
      color: 'from-blue-500 to-blue-600',
      href: '/invoices',
      module: 'invoices',
    },
    {
      name: t('dashboard.orders'),
      value: stats.orders,
      icon: ShoppingCart,
      color: 'from-green-500 to-green-600',
      href: '/orders',
      module: 'orders',
    },
    {
      name: t('dashboard.shipments'),
      value: stats.shipments,
      icon: Truck,
      color: 'from-purple-500 to-purple-600',
      href: '/shipments',
      module: 'shipments',
    },
    {
      name: t('dashboard.openTickets'),
      value: stats.tickets,
      icon: Headphones,
      color: 'from-orange-500 to-orange-600',
      href: '/tickets',
      module: 'tickets',
    },
  ]

  const statCards = allStatCards.filter((card) => enabledModules[card.module])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  // Affichage admin sans ERP
  if (isAdminUser) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div className="bg-blue-50 border-l-4 border-blue-400 p-6 rounded-lg">
          <h3 className="text-lg font-semibold text-blue-800 mb-2">{t('dashboard.adminTitle')}</h3>
          <p
            className="text-blue-700"
            dangerouslySetInnerHTML={{ __html: t('dashboard.adminMessage') }}
          />
          <div className="mt-4 flex gap-3">
            <Link
              to="/admin"
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
            >
              <Shield className="w-4 h-4 mr-2" />
              {t('dashboard.adminButton')}
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Alert for unpaid invoices */}
      {enabledModules.invoices && stats.unpaidInvoices > 0 && (
        <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded-lg animate-slide-down">
          <div className="flex items-start">
            <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 mr-3" />
            <div>
              <h3 className="text-sm font-medium text-amber-800">
                {t('dashboard.unpaidInvoices', { count: stats.unpaidInvoices })}
              </h3>
              <p className="text-sm text-amber-700 mt-1">
                <Link to="/invoices" className="font-medium underline">
                  {t('dashboard.viewInvoices')}
                </Link>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Stats cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((card, index) => {
          const Icon = card.icon
          return (
            <Link
              key={card.name}
              to={card.href}
              className="group bg-white rounded-xl shadow-sm hover:shadow-lg transition-all p-6 border border-gray-100"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{card.name}</p>
                  <p className="text-3xl font-bold text-gray-900 mt-2">{card.value}</p>
                </div>
                <div
                  className={`w-14 h-14 rounded-xl bg-gradient-to-br ${card.color} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform`}
                >
                  <Icon className="w-7 h-7 text-white" />
                </div>
              </div>
            </Link>
          )
        })}
      </div>

      {/* Recent activity */}
      {(enabledModules.invoices || enabledModules.orders) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent invoices */}
          {enabledModules.invoices && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {t('dashboard.recentInvoices')}
                  </h3>
                  <Link
                    to="/invoices"
                    className="text-sm font-medium text-primary-600 hover:text-primary-700"
                  >
                    {t('common.viewAll')}
                  </Link>
                </div>
              </div>
              <div className="divide-y divide-gray-100">
                {recentInvoices.length === 0 ? (
                  <p className="p-6 text-center text-gray-500">{t('dashboard.noInvoices')}</p>
                ) : (
                  recentInvoices.map((invoice) => (
                    <Link
                      key={invoice.id}
                      to={`/invoices/${invoice.id}${invoice._erp_id ? `?erp_id=${invoice._erp_id}` : ''}`}
                      className="block p-4 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-gray-900">{invoice.ref}</p>
                          <p className="text-sm text-gray-500">{formatDate(invoice.date)}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-gray-900">
                            {formatCurrency(invoice.total_incl_tax)}
                          </p>
                          <span
                            className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${
                              invoice.is_paid === 1
                                ? 'bg-green-100 text-green-800'
                                : 'bg-yellow-100 text-yellow-800'
                            }`}
                          >
                            {invoice.is_paid === 1 ? t('dashboard.paid') : t('dashboard.pending')}
                          </span>
                        </div>
                      </div>
                    </Link>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Recent orders */}
          {enabledModules.orders && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {t('dashboard.recentOrders')}
                  </h3>
                  <Link
                    to="/orders"
                    className="text-sm font-medium text-primary-600 hover:text-primary-700"
                  >
                    {t('common.viewAll')}
                  </Link>
                </div>
              </div>
              <div className="divide-y divide-gray-100">
                {recentOrders.length === 0 ? (
                  <p className="p-6 text-center text-gray-500">{t('dashboard.noOrders')}</p>
                ) : (
                  recentOrders.map((order) => (
                    <Link
                      key={order.id}
                      to={`/orders/${order.id}${order._erp_id ? `?erp_id=${order._erp_id}` : ''}`}
                      className="block p-4 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-gray-900">{order.ref}</p>
                          <p className="text-sm text-gray-500">{formatDate(order.date)}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-gray-900">
                            {formatCurrency(order.total_incl_tax)}
                          </p>
                        </div>
                      </div>
                    </Link>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
